<template>
    <transition-group name="pop">
        <template v-for="(cylinder, index) in form.cylinders" :key="index">
            <div v-if="form.qtySelected" class="flex flex-wrap my-4">
                <!-- Cylinder progress dot -->
                <div class="absolute left-0">
                    <div class="px-6">
                        <div class="-ml-px absolute top-0 left-4 w-0.5 h-12" aria-hidden="true" />
                    </div>
                    <span
                        v-if="multipleAllowed"
                        class="h-5 w-5 rounded-full absolute top-14 ml-1.5 text-center font-bold text-xs items-center flex justify-center fadein"
                        :class="[cylinder.a && cylinder.b ? 'bg-green-600 text-white' : 'border-green-600 border bg-white text-green-600']"
                    >
                        {{ index + 1 }}
                    </span>
                </div>

            <div
                class="flex flex-wrap w-full p-4 border-dgreen/50 border rounded-lg"
                :class="{
                    border: index == data.focusIndex,
                    'bg-gray-50': cylinder.a && cylinder.b,
                    'shadow-xl': !cylinder.a || !cylinder.b,
                }"
            >
                <div class="w-full relative pb-4" :class="{ 'min-h-[125px]': (cylinder.a && cylinder.b) || index == data.focusIndex }">
                    <CylinderImage
                        :focused="index === data.focusIndex"
                        :product="product"
                        :image="data.totals?.cylinders?.[index]?.image || false"
                        :default_image="data.totals?.cylinders?.[index]?.default_image || false"
                        :active="data.totals?.cylinders?.[index]?.cylinders_invalid || false"
                        :cylinder="cylinder"
                        :class="data.loading ? 'opacity-50' : 'opacity-100'"
                        class="transition-all"
                    />

                        <div class="hidden md:flex absolute right-0 top-0 items-center justify-end" v-if="multipleAllowed">
                            <button
                            
                                v-if="cylinder.a && cylinder.b"
                                class="p-1 relative group"
                                @click="copy(cylinder)"
                                :class="[cylinder.a && cylinder.b ? 'text-gray-500 cursor-pointer hover:text-gray-800' : 'text-gray-200 cursor-not-allowed']"
                            >
                                <fa :icon="['fal', 'copy']" class="h-5 w-5 transition" />
                                <fa :icon="['fal', 'level-down']" class="h-5 w-5 transition absolute rotate-90 bottom-0 left-0 -ml-1 -mb-4" />
                                <span class="tippy">Kopieër cilinder</span>
                            </button>

                            <button v-if="index > 0" class="cursor-pointer p-1 text-gray-500 hover:text-gray-800 group" @click="remove(index)">
                                <fa :icon="['fal', 'trash-alt']" class="h-5 w-5 transition" />
                                <span class="tippy">Verwijder cilinder</span>
                            </button>
                        </div>
                    </div>

                    <div class="w-full" :class="options.colors.length > 1 && multipleAllowed ? 'md:w-1/3' : 'md:w-1/2'">
                        <!-- 2.1 Formaten -->
                        <cylinder-size
                            :key="data.refreshKey"
                            :focusIndex="data.focusIndex"
                            :options="options.sizes.a"
                            v-model:valueA="cylinder.a"
                            v-model:valueB="cylinder.b"
                            :index="index"
                            :invalid="data.totals?.cylinders_invalid?.includes(index) && errors.cylindersValid"
                            :product="product"
                            :availableSizesB="sizesB"
                        />

                        <div v-if="index === 0" class="mt-2 text-sm">
                            <span class="link"></span>
                            <info-modal title="Hulp nodig bij het opmeten?" :body="text_blocks.cilindermaat" text="Hulp nodig bij het opmeten?" class="" />
                        </div>
                    </div>

                    <div class="flex flex-wrap w-full md:pl-3 mt-4 md:mt-0" :class="options.colors.length > 1 && multipleAllowed ? 'md:w-2/3' : 'md:w-1/2'">
                        <!-- 2.2 Buttons -->
                        <div class="w-1/2 mt-4 md:mt-0" :class="options.colors.length > 1 && multipleAllowed ? 'md:w-1/3' : 'md:w-1/2'">
                            <buttons
                                :key="data.refreshKey"
                                :options="options.buttons"
                                :smartlock_adapters="options.smartlock_adapters"
                                :cylinder="cylinder"
                                v-model:button="cylinder.button"
                                :info="text_blocks.knoppen"
                                :index="index"
                                @showModalSmartlock="showModalSmartlock(index)"
                            />
                        </div>

                        <!-- 2.3 Panicfunction -->
                        <div class="w-1/2 pl-3 mt-4 md:mt-0" :class="options.colors.length > 1 && multipleAllowed ? 'md:w-1/3' : 'md:w-1/2'">
                            <PanicFunction
                                :options="options.panic_function"
                                :cylinder="cylinder"
                                v-model:panic_function="cylinder.panic_function"
                                :info="text_blocks.paniekfunctie"
                                :index="index"
                            />
                        </div>

                        <!-- 2.4 Uitvoering -->
                        <div class="w-full md:w-1/3 md:pl-3" :class="{ hidden: !multipleAllowed }">
                            <cylinder-color
                                :key="data.refreshKey"
                                v-if="options.colors.length > 1"
                                :options="options.colors"
                                :info="text_blocks.kleuren"
                                :index="index"
                                :cylinder="cylinder"
                                v-model:value="cylinder.color"
                            />
                        </div>
                    </div>
                </div>

                <transition name="pop" key="transition">
                    <validation v-if="errors && data.totals?.cylinders_invalid?.includes(index) && errors.cylindersValid" error="Selecteer de maten voor deze cilinder" />
                </transition>
            </div>
        </template>

        <!-- Buttons bottom -->
        <div class="w-full md:inline-flex mt-2" key="buttons" v-if="multipleAllowed">
            <div class="inline-flex space-x-6">
                <button class="flex items-center group cursor-pointer text-gray-600 w-fit" @click="add">
                    <fa :icon="['fal', 'plus']" class="h-4 w-4 mr-2" />
                    <span class="group-hover:text-gray-900 text-sm">Cilinder toevoegen</span>
                </button>

                <button v-if="lastCylinder.a && lastCylinder.b" class="flex items-center group cursor-pointer text-gray-600" @click="copy(lastCylinder)">
                    <fa :icon="['fal', 'copy']" class="h-4 w-4 mr-2" />
                    <span class="group-hover:text-gray-900 text-sm">
                        <span class="hidden md:inline-block">Kopieër cilinder</span>
                    </span>
                </button>

                <button v-if="form.cylinders.length > 1" class="items-center group cursor-pointer text-gray-600" @click="remove(form.cylinders.length - 1)">
                    <fa :icon="['fal', 'trash-alt']" class="h-4 w-4 mr-2" />
                    <span class="group-hover:text-gray-900 text-sm">
                        <span class="hidden md:inline-block">Verwijder cilinder</span>
                    </span>
                </button>
            </div>
        </div>
    </transition-group>
</template>

<script setup>
import Buttons from '@/Components/Configuration/Legacy/Buttons.vue';
import CylinderColor from '@/Components/Configuration/Legacy/CylinderColor.vue';
import CylinderImage from '@/Components/Configuration/Legacy/CylinderImageConverted.vue';
import CylinderSize from '@/Components/Configuration/Legacy/CylinderSize.vue';
import PanicFunction from '@/Components/Configuration/Legacy/PanicFunction.vue';
import { usePage } from '@inertiajs/vue3';
import { computed } from 'vue';

const errors = computed(() => usePage().props.errors);

const emit = defineEmits(['showModalSmartlock', 'loading', 'endLoading', 'setImageLoading', 'endImageLoading']);

const props = defineProps({
    product: Object,
    options: Object,
    data: Object,
    form: Object,
    text_blocks: Object,
    sizesB: Object,
    multipleAllowed: {
        type: Boolean,
        default: true,
    },
});

const lastCylinder = computed(() => props.form.cylinders[props.form.cylinders.length - 1]);

function copy(cylinder) {
    if (!cylinder.a || !cylinder.b) {
        return false;
    }

    props.data.cylinderCopy = true;

    const addCylinder = {
        a: cylinder.a,
        b: cylinder.b,
        button: cylinder.button ? JSON.parse(JSON.stringify(cylinder.button)) : JSON.parse(JSON.stringify(props.options.buttons[0])),
        panic_function: cylinder.panic_function ? JSON.parse(JSON.stringify(cylinder.panic_function)) : JSON.parse(JSON.stringify(props.options.panic_function[0])),
        color: cylinder.color ? JSON.parse(JSON.stringify(cylinder.color)) : props.options.colors.length ? JSON.parse(JSON.stringify(props.options.colors[0])) : false,
    };

    props.form.cylinders.push(addCylinder);
    props.data.cylinderQty++;
}

function add() {

    props.data.cylinderCopy = true;

    const addCylinder = {
        a: 0,
        b: 0,
        button: JSON.parse(JSON.stringify(props.options.buttons[0])),
        panic_function: JSON.parse(JSON.stringify(props.options.panic_function[0])),
        color: false,
    };

    props.form.cylinders.push(addCylinder);
    props.data.cylinderQty++;
}

function remove(index) {
    props.data.cylinderCopy = false;
    props.data.sliceIndex = index;
    props.data.cylinderQty--;
}

function showModalSmartlock(index) {
    emit('showModalSmartlock', index);
}
</script>
